import type { ReactNode } from 'react';
import { describeError } from '../lib/api.js';
import { cx } from './classes.js';
import { StatusGlyph } from './StatusGlyph.js';

/**
 * States (design v1): each says what happened and what to do next; none
 * apologise. `ProblemNote` carries an RFC 7807 detail; `EmptyState` a
 * finding ("not on the register" is a finding, not an error).
 */
export function ProblemNote({ error, action }: { error: unknown; action?: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-btn border border-line bg-surface px-4 py-3.5"
    >
      <StatusGlyph status="attention" />
      <div className="flex min-w-0 flex-col gap-2 text-[14px]">
        <span>{describeError(error)}</span>
        {action ? <span className="flex gap-4 text-[13px]">{action}</span> : null}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  className,
}: {
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('flex flex-col gap-1.5 px-6 py-8', className)}>
      <span className="text-[15px] font-semibold">{title}</span>
      {children ? (
        <span className="max-w-[60ch] text-[13px] leading-relaxed text-mute">
          {children}
        </span>
      ) : null}
    </div>
  );
}

/** The quiet note under a form ("Both registers are re-read…"). */
export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-btn bg-sunk px-4 py-3.5 text-[13px] leading-relaxed text-mute">
      {children}
    </div>
  );
}

/** Per-row entrance delays for `stagger` (literal so Tailwind sees them). */
const STAGGER_DELAY = [
  '[animation-delay:0ms]',
  '[animation-delay:70ms]',
  '[animation-delay:140ms]',
  '[animation-delay:210ms]',
  '[animation-delay:280ms]',
  '[animation-delay:350ms]',
] as const;

/**
 * Loading placeholder rows — no spinner theatre, just the shape of what is
 * coming. `stagger` gives the rows a gentle one-after-another fade-in (the
 * results choreography while the field docks); reduced motion shows them
 * at once.
 */
export function SkeletonRows({
  rows = 3,
  stagger = false,
  className,
}: {
  rows?: number;
  stagger?: boolean;
  className?: string;
}) {
  return (
    <div className={cx('flex flex-col', className)} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className={cx(
            'flex items-center gap-4 px-6 py-[18px]',
            i > 0 && 'border-t border-line',
            stagger && 'animate-fade motion-reduce:animate-none',
            stagger && STAGGER_DELAY[Math.min(i, STAGGER_DELAY.length - 1)],
          )}
        >
          <span className="h-3.5 w-1/3 rounded-[2px] bg-sunk" />
          <span className="h-3 w-1/6 rounded-[2px] bg-sunk" />
        </div>
      ))}
    </div>
  );
}
